import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Share2, Flower2, Flag, Flame } from "lucide-react";
import { api, assetUrl } from "../lib/api";
import { formatDate } from "../lib/format";
import { IMAGES } from "../lib/constants";
import { useSite } from "../context/SiteContext";
import { ShareDialog } from "../components/ShareDialog";
import { FlowerDialog } from "../components/FlowerDialog";
import { CandleDialog } from "../components/CandleDialog";
import { CandleList } from "../components/CandleList";
import { fontStyle } from "../lib/constants";
import { ReportDialog } from "../components/ReportDialog";
import { Lightbox } from "../components/Lightbox";

function useNoIndex(active) {
  useEffect(() => {
    if (!active) return;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [active]);
}

export const NotAvailable = ({ title, text, testId }) => (
  <main id="main" className="mx-auto max-w-2xl px-5 pt-44 pb-32 text-center" data-testid={testId}>
    <h1 className="font-heading text-3xl sm:text-4xl">{title}</h1>
    <p className="mt-5 text-[hsl(var(--muted-foreground))]">{text}</p>
    <Link to="/" className="btn-primary mt-10">←</Link>
  </main>
);

export default function MemorialPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { settings } = useSite();
  const [m, setM] = useState(null);
  const [error, setError] = useState(false);
  const [share, setShare] = useState(false);
  const [candle, setCandle] = useState(false);
  const [candles, setCandles] = useState(null);
  const [flower, setFlower] = useState(false);
  const [report, setReport] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  useNoIndex(m && m.visibility !== "public");

  useEffect(() => {
    const region = (navigator.language || "").split("-")[1];
    api
      .get(`/memorials/${slug}`, { headers: { "x-lang": i18n.language, ...(region ? { "x-region": region } : {}) } })
      .then(({ data }) => setM(data))
      .then(() => api.get(`/memorials/${slug}/candles`).then(({ data }) => setCandles(data)).catch(() => {}))
      .catch(() => setError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) return <NotAvailable title={t("memorial.not_found_title")} text={t("memorial.not_found_text")} testId="memorial-not-found" />;
  if (!m) return <div className="min-h-screen grid place-items-center"><div className="boot-glow" /></div>;

  return (
    <main id="main" data-testid="memorial-page">
      <section className="relative h-[42vh] min-h-[300px] overflow-hidden bg-[var(--dusk)]">
        <img src={IMAGES[m.environment]} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70 kenburns" />
        <div className="absolute inset-0 bg-gradient-to-b from-[rgba(20,16,12,0.4)] to-[hsl(var(--background))]" />
      </section>
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 -mt-40 pb-24">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-5">
            {m.photo ? (
              <button onClick={() => setLightbox(m.gallery.findIndex((g) => g.id === m.photo.id))} className="block w-full" data-testid="memorial-main-photo-btn">
                <img src={assetUrl(m.photo.full)} alt={t("memorial.photo_of", { name: m.name })} className="w-full aspect-[4/5] object-cover rounded-[999px_999px_28px_28px] shadow-[0_40px_80px_-40px_rgba(30,20,10,0.7)]" data-testid="memorial-main-photo" />
              </button>
            ) : (
              <div className="w-full aspect-[4/5] rounded-[999px_999px_28px_28px] bg-[hsl(var(--secondary))]" />
            )}
          </div>
          <div className="md:col-span-7 pb-4">
            {m.visibility !== "public" && <span className="inline-block rounded-full bg-[hsl(var(--secondary))] px-3 py-1 text-xs font-semibold mb-4" data-testid="unlisted-badge">{t("memorial.unlisted_badge")}</span>}
            <p className="eyebrow">{t(`species.${m.species}`)}{m.breed && ` · ${m.breed}`}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-3" dir="auto" data-testid="memorial-name">{m.name}</h1>
            {m.surname && <p className="font-heading text-2xl text-[hsl(var(--muted-foreground))] mt-2" dir="auto" data-testid="memorial-surname">{m.surname}</p>}
            <dl className="flex flex-wrap gap-x-8 gap-y-2 mt-6 text-sm">
              {m.birth_date && <div><dt className="text-[hsl(var(--muted-foreground))]">{t("memorial.born")}</dt><dd className="font-semibold" data-testid="memorial-birth">{formatDate(m.birth_date)}</dd></div>}
              {m.death_date && <div><dt className="text-[hsl(var(--muted-foreground))]">{t("memorial.passed")}</dt><dd className="font-semibold" data-testid="memorial-death">{formatDate(m.death_date)}</dd></div>}
            </dl>
            {m.epitaph && <blockquote className="text-2xl sm:text-3xl leading-snug mt-8 border-s-2 border-[var(--gold)] ps-6" style={fontStyle(m.epitaph_font)} dir="auto" data-testid="memorial-epitaph">{m.epitaph}</blockquote>}
            <div className="flex flex-wrap gap-3 mt-10">
              <Link to={`/explore/${m.environment}?m=${m.slug}`} className="btn-gold" data-testid="visit-stone-btn"><MapPin size={16} /> {t("memorial.visit_stone")}</Link>
              <button className="btn-primary" onClick={() => setShare(true)} data-testid="share-memory-btn"><Share2 size={16} /> {t("memorial.share")}</button>
              {settings.features?.flowers !== false && <button className="btn-ghost" onClick={() => setFlower(true)} data-testid="leave-flower-btn"><Flower2 size={16} /> {t("memorial.leave_flower")}</button>}
              {settings.features?.candles !== false && <button className="btn-ghost" onClick={() => setCandle(true)} data-testid="light-candle-btn"><Flame size={16} /> {t("candle.light")}</button>}
            </div>
            {m.flowers_count > 0 && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-4" data-testid="flowers-count">{t("memorial.flowers_left", { count: m.flowers_count })}</p>}
          </div>
        </div>

        <section className="mt-24" aria-labelledby="gallery-title" data-testid="memorial-gallery">
          <h2 id="gallery-title" className="font-heading text-2xl sm:text-3xl">{t("memorial.gallery")}</h2>
          {m.gallery.length === 0 ? (
            <p className="text-[hsl(var(--muted-foreground))] mt-4">{t("memorial.gallery_empty")}</p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-8">
              {m.gallery.map((g, i) => (
                <li key={g.id}>
                  <button onClick={() => setLightbox(i)} className="group block w-full overflow-hidden rounded-2xl aspect-square bg-[hsl(var(--secondary))]" data-testid={`gallery-thumb-${i}`} aria-label={`${t("memorial.photo_of", { name: m.name })} ${i + 1}`}>
                    <img src={assetUrl(g.thumb)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </button>
                  {g.caption && <p className="text-sm italic mt-2 px-1 text-[hsl(var(--muted-foreground))]" dir="auto" data-testid={`gallery-caption-${i}`}>{g.caption}</p>}
                </li>
              ))}
            </ul>
          )}
        </section>

        {settings.features?.candles !== false && candles && <CandleList data={candles} name={m.name} />}

        <footer className="mt-20 pt-8 border-t flex flex-wrap items-center justify-between gap-4 text-sm text-[hsl(var(--muted-foreground))]">
          <p>{t("memorial.in_env", { env: t(`env.${m.environment}.name`) })} · {t("memorial.created_on", { date: formatDate(m.created_at) })}</p>
          {settings.features?.reports !== false && (
            <button onClick={() => setReport(true)} className="inline-flex items-center gap-2 hover:text-[hsl(var(--foreground))]" data-testid="report-btn"><Flag size={14} /> {t("memorial.report")}</button>
          )}
        </footer>
      </div>
      <ShareDialog open={share} onOpenChange={setShare} slug={m.slug} name={m.name} />
      <CandleDialog open={candle} onOpenChange={setCandle} slug={m.slug} name={m.name} onLit={(count, c) => setCandles((d) => ({ count, items: [c, ...(d?.items || [])] }))} />
      <FlowerDialog open={flower} onOpenChange={setFlower} slug={m.slug} onLeft={(c) => setM({ ...m, flowers_count: c })} />
      <ReportDialog open={report} onOpenChange={setReport} slug={m.slug} />
      <Lightbox images={m.gallery} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} name={m.name} />
    </main>
  );
}
